import { Component } from "@angular/core";
import { AbstractEffTestingStatusComponent } from "../abstract-eff-testing-status-component";
import { EffTableConfig } from "../../common/eff-table/models/eff-table-config.model";
import { EffTableColumnConfig } from "../../common/eff-table/models/eff-table-column-config.model";
import { EvidenceSampleTestStep } from "../models/evidence-sample-step.model";
import { SampleView } from "../models/sample-view.model";
import _ from "underscore";
import { EvidenceSampleEditModel } from "../../../shared/models";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { EffStepHeaderComponent } from "../../common/eff-step-header/eff-step-header.component";
import { AuthorMessageBoxComponent } from "../components/author-message-box/author-message-box.component";
import { EffCheckboxComponent } from "../../common/eff-checkbox/eff-checkbox.component";
import { EffInlineTextEditorComponent } from "../../common/eff-inline-text-editor/eff-inline-text-editor.component";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";
import { EffTableComponent, EffTableColumnDirective } from "../../common/eff-table/eff-table.component";
import { EffTextareaComponent } from "../../common/eff-textarea/eff-textarea.component";
import { DocumentsListComponent } from "../components/documents-list/documents-list.component";
import { EffIconComponent } from "../../common/eff-icon/eff-icon.component";
import { EffButtonComponent } from "../../common/eff-button/eff-button.component";
import { EffTestInstructionsInfoBoxComponent } from "../../info-boxes/eff-test-instructions-info-box/eff-test-instructions-info-box.component";
import { EffEvidenceInstructionsInfoBoxComponent } from "../../info-boxes/eff-evidence-instructions-info-box/eff-evidence-instructions-info-box.component";
import { EffSourceDocumentInfoBoxComponent } from "../../info-boxes/eff-source-document-info-box/eff-source-document-info-box.component";
import { EffControlInfoBoxComponent } from "../../info-boxes/eff-control-info-box/eff-control-info-box.component";
import { CerrixSlideOverComponent } from "@app/shared/cerrix-slide-over/cerrix-slide-over.component";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";

@Component({
    selector: "eff-testing-upload-evidence",
    templateUrl: "./eff-testing-upload-evidence.component.html",
    styleUrls: ["./eff-testing-upload-evidence.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        NgClass,
        NgTemplateOutlet,
        EffStepHeaderComponent,
        AuthorMessageBoxComponent,
        EffCheckboxComponent,
        EffInlineTextEditorComponent,
        DateFormatPipe,
        EffTableComponent,
        EffTableColumnDirective,
        EffTextareaComponent,
        DocumentsListComponent,
        EffIconComponent,
        EffButtonComponent,
        EffTestInstructionsInfoBoxComponent,
        EffEvidenceInstructionsInfoBoxComponent,
        EffSourceDocumentInfoBoxComponent,
        EffControlInfoBoxComponent,
        CerrixSlideOverComponent,
        TranslatePipe,
    ]
})
export class EffTestingUploadEvidenceComponent extends AbstractEffTestingStatusComponent {
    protected loadSamples: boolean = true;
    evidenceUploadValid: boolean = false;

    tableConfig: EffTableConfig = {
        ExpandableRow: true,
        SeparateRows: true,
        RowClassFunc(item: EvidenceSampleTestStep) {
            if (item.isSample) {
                return "sample-row";
            }
            return "";
        },
    };

    displayedColumns: Array<EffTableColumnConfig> = [
        { PropertyName: "name", DisplayedNameKey: "effGrid.columnName", Width: "70%", IsTemplate: true },
        { PropertyName: "evidence", DisplayedNameKey: "effGrid.columnEvidence", IsTemplate: true, Width: "30%" },
    ];

    onChangesDebounce = _.debounce(this.onChanges, 400, false);

    override onLoad() {
        if (this.pageState.isRejected) {
            this.displayedColumns = [
                { PropertyName: "name", DisplayedNameKey: "effGrid.columnName", IsTemplate: true, Width: "40%" },
                {
                    PropertyName: "testerComment",
                    DisplayedNameKey: "effGrid.columnCommentTester",
                    IsTemplate: true,
                    Width: "30%",
                },
                { PropertyName: "score", DisplayedNameKey: "effGrid.columnScore", IsTemplate: true, Width: "30%" },
            ];
        }

        this.viewSamples.forEach((viewSample) => {
            this.setNoEvidenceIfNoTaskDocumentsAndNoDocuments(viewSample.sample);

            viewSample.gridDataSource.data.forEach((gridData) => {
                if (gridData.isSample && gridData.taskData) {
                    this.setNoEvidenceIfNoTaskDocumentsAndNoDocuments(gridData);
                }
            });
        });

        this.validateEvidence();
    }

    setNoEvidenceIfNoTaskDocumentsAndNoDocuments(
        sample: EvidenceSampleEditModel | EvidenceSampleTestStep
    ) {
        if (sample.taskData && sample.taskData.workflowStatus == this.taskWorkflowStatusDone) {
            if (
                (!sample.taskData.documents || sample.taskData.documents.length == 0) &&
                sample.documents.length == 0
            ) {
                sample.noEvidence = true;
            }
        }
    }

    onDocumentsChanged() {
        this.validateEvidence();
    }

    onChanges() {
        this.validateEvidence();
    }

    validateEvidence(showWarning: boolean = false) {
        let valid = true;
        this.viewSamples.forEach((sampleView: SampleView) => {
            if (!this.sampleOrAnyTestStepHasEvidence(sampleView, showWarning)) {
                valid = false;
            }
        });
        this.evidenceUploadValid = valid;
    }

    public sampleOrAnyTestStepHasEvidence(
        sampleView: SampleView,
        showWarning: boolean = false
    ): boolean {
        let sampleItem = sampleView.gridDataSource.data.find((s) => s.isSample);
        let valid = true;

        if (sampleItem.noEvidence) {
            if (
                sampleItem.noEvidenceReason == "" ||
                sampleItem.noEvidenceReason == null ||
                sampleItem.noEvidenceReason == undefined
            ) {
                if (showWarning) {
                    this._toastr.warning(
                        this._translation.t("effTestingUploadEvidence.noEvidenceReasonToast", { sampleName: sampleItem.name })
                    );
                }
                valid = false;
            }
        } else {
            if (
                this.totalSampleDocumentCount(sampleView.sample) == 0 &&
                !sampleView.sample.testSteps.some(
                    (ts) => this.visibleDocuments(ts.documents).length > 0
                )
            ) {
                if (showWarning) {
                    this._toastr.warning(
                        this._translation.t("effTestingUploadEvidence.uploadEvidenceToast", { sampleName: sampleItem.name }),
                        "",
                        { timeOut: 12500 }
                    );
                }
                valid = false;
            }
        }

        return valid;
    }

    hasEvidence(item: EvidenceSampleTestStep) {
        return this.visibleDocuments(item.documents)?.length > 0;
    }

    getEvidenceStatusClass(item: EvidenceSampleTestStep, allItems: EvidenceSampleTestStep[]) {
        if (item.isSample && item.noEvidence) {
            return "status-no-evidence";
        }

        if (this.totalDocumentCount(item) > 0) {
            return "status-has-evidence";
        }

        if (!item.isSample) {
            return "status-evidence-optional";
        } else {
            if (allItems.some((gridItem) => this.totalDocumentCount(gridItem) > 0)) {
                return "status-evidence-optional";
            }
        }

        return "status-needs-evidence";
    }

    getEvidenceStatusText(item: EvidenceSampleTestStep, allItems: EvidenceSampleTestStep[]) {
        if (item.isSample && item.noEvidence) {
            return this._translation.t("effTestingUploadEvidence.noEvidence");
        }

        if (this.totalDocumentCount(item) > 0) {
            return this._translation.t("effTestingUploadEvidence.done");
        }

        if (!item.isSample) {
            return this._translation.t("effTestingUploadEvidence.evidenceOptional");
        } else {
            if (allItems.some((gridItem) => this.totalDocumentCount(gridItem) > 0)) {
                return this._translation.t("effTestingUploadEvidence.evidenceOptional");
            }
        }

        return this._translation.t("effTestingUploadEvidence.evidenceRequired");
    }

    async saveChanges() {
        await this.saveSampleChanges();
    }

    async completeEvidenceUpload() {
        await this._translation.ready();
        this.validateEvidence(true);
        if (!this.evidenceUploadValid) {
            return;
        }

        await this.saveChanges();
        this._workflowDs
            .postWorkflowCompleteEvidenceUpload(this.effectivenessModel.guid)
            .subscribe(() => {
                this.cerrixTab.refresh();
            });
    }

    hasNoEvidenceAndDocuments(item: EvidenceSampleTestStep) {
        return this.visibleDocuments(item.documents).length > 0 && item.noEvidence;
    }
}
